import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faUserSlash } from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-solid-svg-icons'

import {
  Box, Checkbox, ListItem, Text, UserProfileImage,
} from '@domain-group/suit-theme'

import { TaskListModel } from '@models/taskList'

import useTaskList, { useRelations } from '@hooks/useTaskList'
import { useRelations as useUserRelations } from '@hooks/useUser'

type RenderStatusTextParams = {
  checklistScore: {
    available_points: number,
    failed_points: number,
    received_points: number,
  },
}

const renderStatusText = (params: RenderStatusTextParams) => {
  const { checklistScore } = params

  const availablePoints = checklistScore?.available_points ?? 0
  const receivedPoints = checklistScore?.received_points ?? 0
  const failedPoints = checklistScore?.failed_points ?? 0

  if (receivedPoints === 0){
    return 'Not Completed'
  }

  if (failedPoints > 0){
    const percentage = Math.round((receivedPoints / availablePoints) * 100)
    return `Checklist Failed (${percentage}% Passed)`
  }

  return 'Checklist Passed'
}

interface ChecklistProps {
  taskList: TaskListModel,
}

const Checklist = (props: ChecklistProps) => {
  const { taskList } = props

  const taskListPayload = useTaskList(taskList)
  const {
    callbacks: { viewAndProcessChecklist },
    taskList: { checklist_score, completed, title },
  } = taskListPayload

  const { user } = useRelations(taskList)
  const { image } = useUserRelations(user)

  const failedPoints = checklist_score?.failed_points ?? 0

  return (
    <ListItem
      boxProps={{
        alignItems: 'center',
        as: 'a',
        borderBottom: '1px dashed',
        fontSize: 'small',
        padding: 'large',
      }}
      onClick={() => viewAndProcessChecklist()}
      showAngleIcon
    >
      <Checkbox
        checked={completed}
        color={failedPoints > 0 ? 'red' : 'green'}
        fontSize="small"
        iconChecked={failedPoints > 0 ? faTimesCircle : faCheckCircle}
        style={{ cursor: 'default' }}
      />

      <Box alignItems="center" maxWidth="calc(100% - 30px)">
        <Box flexDirection="column" maxWidth="80%" paddingRight="large">
          <Text variant="ellipsis">{title}</Text>

          <Text
            color="bodyFontLightColor"
            display={['none', 'block']}
            fontSize="xsmall"
            marginTop="small"
            variant="ellipsis"
          >
            {renderStatusText({ checklistScore: checklist_score })}
          </Text>
        </Box>

        <Box maxWidth="40%" overflow="hidden" justifyContent="flex-end" overflow="visible">
          <Box marginLeft="auto" width="auto">
            <UserProfileImage
              boxProps={{ display: 'inline-flex' }}
              hintComponent={<UserProfileImage.Hint>{user.full_name || 'Unassigned'}</UserProfileImage.Hint>}
              imageUrl={image.thumb_url}
            >
              <FontAwesomeIcon icon={user.id ? faUser : faUserSlash} />
            </UserProfileImage>
          </Box>
        </Box>
      </Box>
    </ListItem>
  )
}

export default Checklist
